/**
 * Feb 27, 2026 · Excess Inventory Summary
 *
 * Every GRN that over-supplies a PO auto-splits the surplus into a
 * flagged `is_excess=True` inventory lot with default
 * `excess_status="Held for Return"`. This page is the procurement
 * dashboard — see every open excess lot at a glance and either keep
 * it (Approve for Consumption) or send it via Purchase Return.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useRbac } from "@/lib/rbac";
import {
  StackIcon,
  ArrowsClockwiseIcon,
  CheckCircleIcon,
  WarningIcon,
  TruckIcon,
} from "@phosphor-icons/react";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
const money = (n) => Number(n || 0).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });

const STATUS_CLS = {
  "Held for Return": "border-amber-500/50 text-amber-300 bg-amber-500/10",
  "Approved for Consumption": "border-emerald-500/50 text-emerald-300 bg-emerald-500/10",
};

const FILTERS = ["ALL", "Held for Return", "Approved for Consumption"];

export default function ExcessInventory() {
  const { can: rbacCan } = useRbac();
  const canEditExcess = rbacCan("excess_purchase", "edit");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ rows: [], totals: {} });
  const [filter, setFilter] = useState("ALL");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: r }, { data: s }] = await Promise.all([
        api.get("/excess-purchase/lots", { params: filter === "ALL" ? {} : { status: filter } }),
        api.get("/excess-purchase/summary"),
      ]);
      setRows(r || []);
      setSummary(s || { rows: [], totals: {} });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const toggleStatus = async (r) => {
    const next = r.excess_status === "Held for Return" ? "Approved for Consumption" : "Held for Return";
    const notes = window.prompt(`Set ${r.batch_no} to "${next}"?\n\nAdd a note (optional):`, "");
    if (notes === null) return;
    try {
      await api.post(`/excess-purchase/${r.lot_id}/status`, { status: next, notes });
      toast.success(`${r.batch_no} · ${next}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const totalBalance = useMemo(() => rows.reduce((s, r) => s + Number(r.returnable_balance || 0), 0), [rows]);
  const totalValue = useMemo(() => rows.reduce((s, r) => s + Number(r.value || 0), 0), [rows]);

  return (
    <div className="p-6 space-y-5" data-testid="excess-inventory-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1 flex items-center gap-2">
            <StackIcon size={11} weight="bold" />
            Purchase &amp; Inventory · Excess Inventory
          </div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white">Excess Inventory Summary</h1>
          <div className="text-xs text-zinc-500 mt-1 max-w-2xl">
            Every GRN that over-supplies a PO auto-splits the surplus into a flagged Excess lot. Decide
            whether to keep each lot for consumption or send it back via a Purchase Return.
          </div>
        </div>
        <button data-testid="ei-refresh" onClick={load} disabled={loading}
          className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-white text-sm rounded-sm flex items-center gap-2">
          <ArrowsClockwiseIcon size={14} weight="bold" />Refresh
        </button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="ei-kpis">
        {[
          { label: "Open Lots", value: summary.totals?.lots || 0, color: "text-white" },
          { label: "Suppliers", value: summary.totals?.suppliers || 0, color: "text-sky-300" },
          { label: "Held for Return", value: `${fmt(summary.totals?.qty_held)}`, sub: money(summary.totals?.value_held), color: "text-amber-300" },
          { label: "Approved for Consumption", value: `${fmt(summary.totals?.qty_approved)}`, sub: money(summary.totals?.value_approved), color: "text-emerald-300" },
        ].map((k, i) => (
          <div key={i} className="tactical-card p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{k.label}</div>
            <div className={`text-2xl font-mono mt-1 ${k.color}`}>{k.value}</div>
            {k.sub && <div className="text-[10px] text-zinc-500 font-mono mt-0.5">{k.sub}</div>}
          </div>
        ))}
      </div>

      {/* Filter strip */}
      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map(f => (
          <button
            key={f}
            data-testid={`ei-filter-${f.replace(/ /g, "-").toLowerCase()}`}
            onClick={() => setFilter(f)}
            className={`h-8 px-3 text-xs font-mono uppercase tracking-widest rounded-sm border ${
              filter === f
                ? "border-[var(--accent)] bg-[var(--accent)]/20 text-white"
                : "border-[var(--border)] text-zinc-400 hover:bg-[var(--bg-2)]"
            }`}
          >
            {f}
          </button>
        ))}
        <div className="ml-auto text-[11px] font-mono uppercase tracking-widest text-zinc-500">
          {rows.length} lots · Balance <span className="text-amber-300">{fmt(totalBalance)}</span> · Value <span className="text-zinc-200">{money(totalValue)}</span>
        </div>
      </div>

      {/* Lots table */}
      <div className="tactical-card overflow-x-auto" data-testid="ei-lots-table">
        <table className="w-full text-xs">
          <thead className="bg-[var(--bg-2)]">
            <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
              <th className="text-left px-3 py-2">Batch (EXC)</th>
              <th className="text-left px-3 py-2">RM</th>
              <th className="text-left px-3 py-2">Supplier</th>
              <th className="text-left px-3 py-2">PO · Invoice</th>
              <th className="text-right px-3 py-2">Excess Qty</th>
              <th className="text-right px-3 py-2">Balance</th>
              <th className="text-right px-3 py-2">Value</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-center px-3 py-2 w-32">—</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="text-center py-6 text-zinc-500">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-zinc-500">
                <WarningIcon size={22} weight="fill" className="inline-block mr-2 text-emerald-400" />
                No excess lots — every PO is landing on target.
              </td></tr>
            )}
            {rows.map(r => (
              <tr key={r.lot_id} data-testid={`ei-lot-${r.lot_id}`} className="border-b border-[var(--border)] hover:bg-[var(--bg-2)]/40">
                <td className="px-3 py-2 font-mono text-[var(--accent)]">{r.batch_no}</td>
                <td className="px-3 py-2 text-zinc-300 truncate max-w-[220px]" title={r.rm_code}>{r.rm_code}</td>
                <td className="px-3 py-2 text-zinc-300">{r.supplier_name}</td>
                <td className="px-3 py-2 text-[10px] font-mono text-zinc-400">
                  {r.po_no || "—"}
                  <div className="text-zinc-500">{r.invoice_no || "—"} · {r.received_date}</div>
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-amber-300">{fmt(r.qty_received)}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-white">{fmt(r.returnable_balance)}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-300">{money(r.value)}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[9px] font-mono uppercase tracking-widest ${STATUS_CLS[r.excess_status] || ""}`}>{r.excess_status}</span>
                </td>
                <td className="px-3 py-2 text-center">
                  <button data-testid={`ei-toggle-${r.lot_id}`} onClick={() => toggleStatus(r)} disabled={!canEditExcess}
                    title={canEditExcess ? undefined : "Requires Excess Purchase · Edit permission"}
                    className="h-7 px-2 border border-[var(--border)] hover:bg-[var(--bg-2)] disabled:opacity-40 disabled:cursor-not-allowed rounded-sm text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1">
                    {r.excess_status === "Held for Return" ? (<><CheckCircleIcon size={11} weight="bold" />Approve</>) : (<><TruckIcon size={11} weight="bold" />Hold</>)}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Supplier summary */}
      <div className="tactical-card" data-testid="ei-supplier-summary">
        <div className="px-4 py-2 border-b border-[var(--border)] text-[11px] font-mono uppercase tracking-widest text-zinc-400">By Supplier</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[var(--bg-2)]">
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                <th className="text-left px-3 py-2">Supplier</th>
                <th className="text-right px-3 py-2">Lots</th>
                <th className="text-right px-3 py-2">Held Qty</th>
                <th className="text-right px-3 py-2">Held Value</th>
                <th className="text-right px-3 py-2">Approved Qty</th>
                <th className="text-right px-3 py-2">Approved Value</th>
              </tr>
            </thead>
            <tbody>
              {(summary.rows || []).length === 0 && <tr><td colSpan={6} className="text-center py-6 text-zinc-500">No excess supply on record.</td></tr>}
              {(summary.rows || []).map(r => (
                <tr key={r.supplier_id} className="border-b border-[var(--border)]">
                  <td className="px-3 py-2 text-zinc-200">{r.supplier_name}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-white">{r.lots}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-amber-300">{fmt(r.qty_held)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-300">{money(r.value_held)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-300">{fmt(r.qty_approved)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-300">{money(r.value_approved)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
